import { fromFirestoreDoc } from "@/lib/firestoreDoc";
import {
  addDoc,
  collection,
  collectionGroup,
  doc,
  DocumentData,
  DocumentReference,
  getDocs,
  getDoc,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  ActivitySummary,
  DashboardAccessLog,
  DateRange,
  OrganizationActivity,
  ResolvedUser,
  TaskPathComponents,
  TaskWithContext,
  TraitSearchResult,
} from '@/types/adminActivity';

/**
 * Fetch a set of Firestore documents by path and extract a named field into a Map.
 * Deduplicates paths and silently skips missing documents.
 */
async function fetchFieldByPaths(
  paths: Iterable<string>,
  field: string,
  fallback: string
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  const uniquePaths = Array.from(new Set(paths));

  await Promise.all(
    uniquePaths.map(async (path) => {
      try {
        const snap = await getDoc(doc(db, path));
        if (snap.exists()) {
          result.set(path, snap.data()[field] || fallback);
        }
      } catch (error) {
        console.error(`Error fetching ${field} from ${path}:`, error);
      }
    })
  );

  return result;
}

/**
 * Parse a task document path to extract org, trial, experiment, and task IDs.
 * Path format: organizations/{orgId}/trials/{trialId}/experiments/{experimentId}/tasks/{taskId}
 */
export function parseTaskPath(taskRef: DocumentReference): TaskPathComponents | null {
  const pathSegments = taskRef.path.split('/');

  if (pathSegments.length !== 8 || pathSegments[0] !== 'organizations' || pathSegments[2] !== 'trials' || pathSegments[4] !== 'experiments' || pathSegments[6] !== 'tasks') {
    console.warn('Invalid task path format:', taskRef.path);
    return null;
  }

  return {
    orgId: pathSegments[1],
    trialId: pathSegments[3],
    experimentId: pathSegments[5],
    taskId: pathSegments[7],
  };
}

/**
 * Fetch all tasks updated within a date range across all organizations.
 * Uses updatedAt rather than createdAt so that tasks with recent modifications
 * (new data collection, state changes, etc.) surface even if created long ago.
 * updatedAt defaults to createdAt on new tasks, so newly created tasks are included too.
 *
 * NOTE: This query is unbounded (no limit/pagination). Task documents are small (~1-2KB),
 * so Firestore's 10MB response limit accommodates ~5,000-10,000 tasks per query. This is
 * more than sufficient for current scale. If task volume grows significantly, add
 * limit() + startAfter() cursor-based pagination here.
 */
/**
 * A raw `tasks` collection-group hit: the stored document, its id, and the
 * `ref` — which is the only thing that says which org/trial/experiment the task
 * came from, since a collection-group query flattens that hierarchy away.
 */
type TaskCollectionGroupHit = DocumentData & { id: string; ref: DocumentReference };

export async function fetchTasksInDateRange(dateRange: DateRange): Promise<any[]> {
  const startTimestamp = Timestamp.fromDate(dateRange.start);
  const endTimestamp = Timestamp.fromDate(dateRange.end);

  const tasksQuery = query(
    collectionGroup(db, 'tasks'),
    where('updatedAt', '>=', startTimestamp),
    where('updatedAt', '<=', endTimestamp),
    orderBy('updatedAt', 'desc')
  );

  const snapshot = await getDocs(tasksQuery);
  return snapshot.docs.map(doc => fromFirestoreDoc<TaskCollectionGroupHit>(doc, "id", { ref: doc.ref }));
}

/**
 * Resolve user document references to display names and emails.
 * Returns a Map of uid -> ResolvedUser.
 */
export async function resolveUserReferences(userRefs: DocumentReference[]): Promise<Map<string, ResolvedUser>> {
  const userMap = new Map<string, ResolvedUser>();
  const uniquePaths = Array.from(new Set(userRefs.map(ref => ref.path)));

  await Promise.all(
    uniquePaths.map(async (path) => {
      try {
        const userDoc = await getDoc(doc(db, path));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const uid = userDoc.id;

          const displayName =
            data.displayName ||
            (data.firstName || data.lastName
              ? `${data.firstName || ''} ${data.lastName || ''}`.trim()
              : data.email || 'Unknown User');

          userMap.set(uid, { uid, displayName, email: data.email || 'unknown@example.com' });
        }
      } catch (error) {
        console.error('Error fetching user:', path, error);
      }
    })
  );

  return userMap;
}

/**
 * Resolve organization, trial, and experiment names from task document paths.
 * Returns Maps keyed by document path (or orgId for orgs) -> name.
 */
export async function resolveContextNames(tasks: any[]): Promise<{
  orgMap: Map<string, string>;
  trialMap: Map<string, string>;
  experimentMap: Map<string, string>;
}> {
  const orgPaths = new Set<string>();
  const trialPaths = new Set<string>();
  const experimentPaths = new Set<string>();

  for (const task of tasks) {
    const pc = parseTaskPath(task.ref);
    if (!pc) continue;

    orgPaths.add(`organizations/${pc.orgId}`);
    trialPaths.add(`organizations/${pc.orgId}/trials/${pc.trialId}`);
    experimentPaths.add(`organizations/${pc.orgId}/trials/${pc.trialId}/experiments/${pc.experimentId}`);
  }

  const [orgMapByPath, trialMap, experimentMap] = await Promise.all([
    fetchFieldByPaths(orgPaths, 'name', 'Unknown Organization'),
    fetchFieldByPaths(trialPaths, 'name', 'Unknown Trial'),
    fetchFieldByPaths(experimentPaths, 'name', 'Unknown Experiment'),
  ]);

  // Re-key org map from full path to just orgId for downstream consumers
  const orgMap = new Map<string, string>();
  for (const [path, name] of orgMapByPath) {
    const orgId = path.split('/')[1];
    orgMap.set(orgId, name);
  }

  return { orgMap, trialMap, experimentMap };
}

/**
 * Fetch entry config (trait) names from document references.
 * Returns a Map of ref path -> trait name.
 */
export async function fetchEntryConfigNames(entryConfigRefs: DocumentReference[]): Promise<Map<string, string>> {
  const paths = entryConfigRefs.map(ref => ref.path);
  return fetchFieldByPaths(paths, 'name', 'Unknown Trait');
}

const UNKNOWN_USER: ResolvedUser = { uid: 'unknown', displayName: 'Unknown User', email: 'unknown@example.com' };

/**
 * Build TaskWithContext objects from raw task data with resolved references.
 */
export function buildTasksWithContext(
  tasks: any[],
  userMap: Map<string, ResolvedUser>,
  orgMap: Map<string, string>,
  trialMap: Map<string, string>,
  experimentMap: Map<string, string>,
  traitNameMap: Map<string, string>
): TaskWithContext[] {
  return tasks.reduce<TaskWithContext[]>((result, task) => {
    const pc = parseTaskPath(task.ref);
    if (!pc) return result;

    const { orgId, trialId, experimentId, taskId } = pc;

    const createdByUser = task.createdBy?.id ? userMap.get(task.createdBy.id) : undefined;
    const updatedByUser = task.updatedBy?.id ? userMap.get(task.updatedBy.id) : undefined;

    const configs = task.dataCollectionConfigs ?? [];
    const traitNames = configs
      .map((config) => config.entryConfigRef ? traitNameMap.get(config.entryConfigRef.path) : undefined)
      .filter(Boolean) as string[];
    const entryConfigPaths = configs
      .map((config) => config.entryConfigRef?.path)
      .filter(Boolean) as string[];

    result.push({
      taskId,
      name: task.name || 'Unnamed Task',
      state: task.state || 'in-progress',
      createdAt: task.createdAt,
      updatedAt: task.updatedAt,
      orgId,
      orgName: orgMap.get(orgId) || 'Unknown Organization',
      trialId,
      trialName: trialMap.get(`organizations/${orgId}/trials/${trialId}`) || 'Unknown Trial',
      experimentId,
      experimentName: experimentMap.get(`organizations/${orgId}/trials/${trialId}/experiments/${experimentId}`) || 'Unknown Experiment',
      createdBy: createdByUser || { ...UNKNOWN_USER, uid: task.createdBy?.id || 'unknown' },
      updatedBy: updatedByUser,
      entryConfigName: traitNames.length > 0 ? traitNames.join(', ') : 'No Traits',
      entryConfigPaths,
    });

    return result;
  }, []);
}

/** Aggregate tasks into per-organization summaries and overall statistics. */
export function aggregateActivityData(tasks: TaskWithContext[]): ActivitySummary {
  const orgActivities = new Map<string, OrganizationActivity>();
  let completedTasks = 0;
  let inProgressTasks = 0;

  for (const task of tasks) {
    if (!orgActivities.has(task.orgId)) {
      orgActivities.set(task.orgId, { orgId: task.orgId, orgName: task.orgName, taskCount: 0, tasks: [] });
    }

    const orgActivity = orgActivities.get(task.orgId)!;
    orgActivity.taskCount++;
    orgActivity.tasks.push(task);

    if (task.state === 'completed') completedTasks++;
    else if (task.state === 'in-progress') inProgressTasks++;
  }

  return {
    totalOrganizations: orgActivities.size,
    totalTasks: tasks.length,
    completedTasks,
    inProgressTasks,
    organizationActivities: Array.from(orgActivities.values()),
  };
}

const EMPTY_SUMMARY: ActivitySummary = {
  totalOrganizations: 0,
  totalTasks: 0,
  completedTasks: 0,
  inProgressTasks: 0,
  organizationActivities: [],
};

/**
 * Normalize a field that may be a string path or DocumentReference into a DocumentReference.
 * Legacy data may store paths as plain strings instead of Firestore references.
 */
function normalizeRef(value: any, fallbackPath?: string): DocumentReference | null {
  if (!value) return null;
  if (typeof value === 'string') return doc(db, fallbackPath ?? value);
  return value;
}

/**
 * Normalize all references on raw task documents and collect user/trait refs.
 * Mutates tasks in place (normalizing createdBy, updatedBy, entryConfigRef fields).
 */
function normalizeTaskReferences(tasks: any[]): { userRefs: DocumentReference[]; traitRefs: DocumentReference[] } {
  const userRefs: DocumentReference[] = [];
  const traitRefs: DocumentReference[] = [];

  for (const task of tasks) {
    task.createdBy = normalizeRef(task.createdBy);
    if (task.createdBy) userRefs.push(task.createdBy);

    task.updatedBy = normalizeRef(task.updatedBy);
    if (task.updatedBy) userRefs.push(task.updatedBy);

    const taskPath = parseTaskPath(task.ref);
    if (Array.isArray(task.dataCollectionConfigs) && taskPath) {
      for (const config of task.dataCollectionConfigs) {
        if (config.entryConfigRef) {
          const fallbackPath = typeof config.entryConfigRef === 'string' && !config.entryConfigRef.includes('/')
            ? `organizations/${taskPath.orgId}/entryConfigs/${config.entryConfigRef}`
            : undefined;
          config.entryConfigRef = normalizeRef(config.entryConfigRef, fallbackPath);
          if (config.entryConfigRef) traitRefs.push(config.entryConfigRef);
        }
      }
    }
  }

  return { userRefs, traitRefs };
}

/** Resolve all references and build TaskWithContext[] from raw task data. */
async function resolveAndBuildTasks(tasks: any[]): Promise<TaskWithContext[]> {
  const { userRefs, traitRefs } = normalizeTaskReferences(tasks);

  const [userMap, { orgMap, trialMap, experimentMap }, traitNameMap] = await Promise.all([
    resolveUserReferences(userRefs),
    resolveContextNames(tasks),
    fetchEntryConfigNames(traitRefs),
  ]);

  return buildTasksWithContext(tasks, userMap, orgMap, trialMap, experimentMap, traitNameMap);
}

/**
 * Fetch all activity data for a date range across all organizations.
 */
export async function fetchActivityData(dateRange: DateRange): Promise<ActivitySummary> {
  const tasks = await fetchTasksInDateRange(dateRange);

  if (tasks.length === 0) return EMPTY_SUMMARY;

  const tasksWithContext = await resolveAndBuildTasks(tasks);
  return aggregateActivityData(tasksWithContext);
}

/**
 * Fetch all entryConfigs across all organizations via collection group query.
 * Returns TraitSearchResult[] with resolved org names.
 */
export async function fetchAllEntryConfigs(): Promise<TraitSearchResult[]> {
  const configsQuery = query(collectionGroup(db, 'entryConfigs'));
  const snapshot = await getDocs(configsQuery);

  if (snapshot.empty) return [];

  // Collect unique org paths for name resolution
  const orgPaths = new Set<string>();
  const rawConfigs = snapshot.docs.map(docSnap => {
    const pathSegments = docSnap.ref.path.split('/');
    const orgId = pathSegments[1];
    orgPaths.add(`organizations/${orgId}`);
    return { docSnap, orgId };
  });

  const orgNameMap = await fetchFieldByPaths(orgPaths, 'name', 'Unknown Organization');

  return rawConfigs.map(({ docSnap, orgId }) => {
    const data = docSnap.data();
    const orgPath = `organizations/${orgId}`;
    return {
      id: docSnap.id,
      name: data.name || 'Unnamed Trait',
      type: data.type || 'text',
      orgId,
      orgName: orgNameMap.get(orgPath) || 'Unknown Organization',
      path: docSnap.ref.path,
      isSystem: data.isSystem ?? false,
      isArchived: data.isArchived ?? false,
    };
  });
}

/**
 * Fetch all tasks across all organizations via collection group query (no date filter).
 * Returns TaskWithContext[] with full context resolution.
 */
export async function fetchAllTasksForTraitSearch(): Promise<TaskWithContext[]> {
  const tasksQuery = query(
    collectionGroup(db, 'tasks'),
    orderBy('updatedAt', 'desc')
  );
  const snapshot = await getDocs(tasksQuery);
  const tasks = snapshot.docs.map(docSnap => fromFirestoreDoc<TaskCollectionGroupHit>(docSnap, "id", { ref: docSnap.ref }));

  if (tasks.length === 0) return [];

  return resolveAndBuildTasks(tasks);
}

/** Log dashboard access to Firestore for audit trail. */
export async function logDashboardAccess(
  userId: string,
  email: string,
  dateRange: DateRange,
  organizationFilter: string | null
): Promise<void> {
  try {
    const logEntry: DashboardAccessLog = {
      userId,
      email,
      timestamp: Timestamp.now(),
      dateRangeStart: Timestamp.fromDate(dateRange.start),
      dateRangeEnd: Timestamp.fromDate(dateRange.end),
      organizationFilter,
    };

    await addDoc(collection(db, 'adminDashboardAccessLog'), logEntry);
    console.log('Dashboard access logged:', logEntry);
  } catch (error) {
    console.error('Error logging dashboard access:', error);
  }
}
